//// Application Packages
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { object, string } from 'yup'
import axios from 'axios'
import { translateField } from '../../functions/field-functions'
import { extractSubmissionPage } from '../../functions/page-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateAccountData,
    updateAccountValidity,
    deleteAccountData,
    selectAccount,
} from '../../reducers/accountSlice'

//// Application Components
import DividerComponent from '../../components/simple-components/divider-component'
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'
import ModalComponent from '../../components/conditional-components/modal-component'

//// Carbon Components
import { Form, TextInput } from '@carbon/react'

//// Carbon Icons

//// Static Constants
const successMessage = () => {
    return `
<p>
Your temporary account password will be emailed to you shortly.
</p>
`
}

//////// Component
const ResetPasswordPage = () => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})
    const [answering, setAnswering] = useState(false)
    const [modalData, setModalData] = useState({})
    const [modalOpen, setModalOpen] = useState(false)

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const accountData = useSelector(selectAccount)

    //// Slice Data
    const digital_name = settingsData?.['page']?.['digital_name']
    const written_name = settingsData?.['page']?.['written_name']
    const submit_route = settingsData?.['page']?.['submit_route']
    const fields = settingsData?.['page']?.['fields']

    //////// Search Params

    //////// Methods

    // Orchestrate Password Reset Query Data (Integration)
    const orchestratePasswordResetQuery = async (accountData) => {
        try {
            if (accountData) {
                const base_url = `${process.env.REACT_APP_BASE_URL}`
                const api_endpoint = `${process.env.REACT_APP_ORCHESTRATION_ENDPOINT_PROXY_ACCOUNT_PASSWORD_RESET_QUERY}`
                const url = base_url + api_endpoint
                const { userid, UserID } = accountData

                // Create Request Based On Present Data
                if (userid || UserID) {
                    // Check In Request Object
                    const req = {}

                    // Set Parameters To Request Object
                    req.prefix = 'userid'

                    // Check For Request Identifier
                    if (userid) {
                        // Set Parameters To Request Object
                        req.value = userid
                    } else if (UserID) {
                        // Set Parameters To Request Object
                        req.value = UserID
                    }

                    // Check Request Objects Parameters
                    if (req?.['prefix'] && req?.['value']) {
                        const { data } = await axios.post(url, req)
                        if (data) {
                            return data
                        } else {
                            console.log(
                                'Account Reset Password (Query): Response Is Undefined'
                            )
                            return null
                        }
                    } else {
                        console.log(
                            'Account Reset Password (Query): Request Parameters Are Undefined'
                        )
                        return null
                    }
                } else {
                    console.log(
                        'Account Reset Password (Query): Request Is Undefined'
                    )
                    return null
                }
            } else {
                console.log(
                    'Account Reset Password (Query): Request Data Is Undefined'
                )
                return null
            }
        } catch (error) {
            console.warn('Orchestrate Password Reset Query Error:', error)
            return null
        }
    }

    // Orchestrate Password Reset Answer Data (Integration)
    const orchestratePasswordResetAnswer = async (accountData) => {
        try {
            if (digital_name && accountData) {
                const base_url = `${process.env.REACT_APP_BASE_URL}`
                const api_endpoint = `${process.env.REACT_APP_ORCHESTRATION_ENDPOINT_PROXY_ACCOUNT_PASSWORD_RESET_ANSWER}`
                const url = base_url + api_endpoint
                const { userid, UserID, SecretAnswer } = accountData

                // Create Request Based On Present Data
                if ((userid || UserID) && SecretAnswer) {
                    // Check In Request Object
                    const req = {}

                    // Check For Request Identifier
                    if (userid && SecretAnswer) {
                        req.userid = userid
                        req.secretanswer = SecretAnswer
                    } else if (UserID && SecretAnswer) {
                        req.userid = UserID
                        req.secretanswer = SecretAnswer
                    }

                    // Check For Request Parameters
                    if (req?.['userid'] && req?.['secretanswer']) {
                        try {
                            const { data } = await axios.post(url, req)
                            if (data) {
                                return data
                            } else {
                                console.log(
                                    'Account Reset Password (Answer): Response Is Undefined'
                                )
                                return null
                            }
                        } catch (error) {
                            console.warn(
                                'Account Reset Password (Answer) Error:',
                                error
                            )
                            return null
                        }
                    } else {
                        console.log(
                            'Account Reset Password (Answer): Request Parameters Are Undefined'
                        )
                        return null
                    }
                } else {
                    console.log(
                        'Account Temporary Password (Answer): Request Parameters Are Undefined'
                    )

                    return null
                }
            } else {
                console.log(
                    'Account Temporary Password (Answer): Request Data Is Undefined'
                )

                return null
            }
        } catch (error) {
            console.warn('Orchestrate PasswordResetAnswer Error:', error)
            return null
        }
    }

    // Re-Validate Data (Utility)
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateAccountValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    // Validate Data (Check)
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            if (
                !accountData?.[digital_name]?.values?.SecretQuestion &&
                !accountData?.[digital_name]?.values?.SecretAnswer
            ) {
                schema.yup = object({
                    UserID: string()
                        .min(0)
                        .max(50)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                })
                schema.valid = {
                    UserID: false,
                    UserIDErrMsg: '',
                }
            } else if (
                accountData?.[digital_name]?.values?.SecretQuestion &&
                accountData?.[digital_name]?.values?.SecretAnswer
            ) {
                schema.yup = object({
                    UserID: string()
                        .min(0)
                        .max(50)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    SecretAnswer: string()
                        .min(0)
                        .max(100)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                })
                schema.valid = {
                    UserID: false,
                    UserIDErrMsg: '',
                    SecretAnswer: false,
                    SecretAnswerErrMsg: '',
                }
            }
            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateAccountValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateField(fields, el?.path)
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateAccountValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    //////// Functional Methods

    // Handle Data + Redact Data (Method)
    const handleData = (e) => {
        if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateAccountData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateAccountData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        }
        //// Start Watson Assistant Journey

        //// End Watson Assistant Journey
    }

    // Handle Clear Data (Hard Reset)
    const handleClearData = () => {
        //// Perform Browser Effects

        localStorage.clear()
        sessionStorage.clear()

        //// End Perform Browser Effects

        //// Perform Data Clearing

        dispatch(
            updateAccountData({
                digital_name: digital_name,
                values: {
                    UserID: '',
                    SecretAnswer: '',
                },
            })
        )

        dispatch(
            deleteAccountData({
                digital_name: digital_name,
            })
        )

        //// End Data Clearing

        //// Perform Browser Effects

        console.clear()
        window.location.reload()

        //// End Perform Browser Effects
    }

    // Handle Submission (Method)
    const handleSubmit = async () => {
        try {
            console.info('Account Reset Password: Submitted')

            // Extract Account Data From Redux
            const submit = await extractSubmissionPage(
                digital_name,
                accountData
            )

            // Validate Account Submission Data
            const valid = await validation(submit)

            // Check If Account Data Is Valid
            if (valid) {
                console.info('Account Reset Password: Passed Valid')
                setLoading(true)

                if (
                    !accountData?.[digital_name]?.['values']?.[
                        'SecretQuestion'
                    ] &&
                    !accountData?.[digital_name]?.['values']?.['SecretAnswer']
                ) {
                    // Send Orchestrate Password Reset Query Request
                    const orchestrateQuery =
                        await orchestratePasswordResetQuery(submit)

                    console.info(
                        'Account Registration: Passed Orchestrate Query'
                    )

                    // Check For Secret Question
                    if (
                        orchestrateQuery?.['secret_question_exists'] &&
                        orchestrateQuery?.['secretquestion']
                    ) {
                        console.info(
                            'Account Password Reset: Success (Orchestrate Query)'
                        )

                        setLoading(false)
                        dispatch(
                            updateAccountData({
                                digital_name: digital_name,
                                values: {
                                    SecretQuestion:
                                        orchestrateQuery.secretquestion,
                                },
                            })
                        )
                        setNotification({
                            type: 'inline',
                            visible: false,
                            kind: 'success',
                            title: 'Account Password Reset Success:',
                            subtitle: 'Please Answer Your Secret Question',
                        })
                        setAnswering(true)
                    } else {
                        console.info('Account Password Reset: Failed')

                        setLoading(false)
                        setNotification({
                            type: 'inline',
                            visible: true,
                            kind: 'error',
                            title: 'Account Password Reset Failed:',
                            subtitle: 'Account User Id Not Found',
                        })

                        console.log(
                            'Account Password Reset Failed: Failed To Orchestrate Query'
                        )
                    }
                } else if (
                    accountData?.[digital_name]?.['values']?.[
                        'SecretQuestion'
                    ] &&
                    accountData?.[digital_name]?.['values']?.['SecretAnswer']
                ) {
                    // Send Orchestrate Password Reset Answer Request
                    const orchestrateAnswer =
                        await orchestratePasswordResetAnswer(submit)

                    console.info(
                        'Account Registration: Passed Orchestrate Answer',
                        orchestrateAnswer
                    )

                    // Check For Secret Answer
                    if (
                        orchestrateAnswer?.['password_reset'] &&
                        orchestrateAnswer?.['password_reset_email_sent']
                    ) {
                        console.info('Account Password Reset: Success')

                        setLoading(false)
                        setModalOpen(true)
                        setModalData({
                            open: true,
                            alert: true,
                            label: 'Account Password',
                            heading: 'Successfully Reset Your Password',
                            paragraph: successMessage(),
                            submit_label: 'Login',
                            around_close: true,
                        })
                        setNotification({
                            type: 'inline',
                            visible: false,
                            kind: '',
                            title: '',
                            subtitle: '',
                        })
                    } else if (
                        orchestrateAnswer?.['password_reset'] &&
                        !orchestrateAnswer?.['password_reset_email_sent']
                    ) {
                        console.info('Account Password Reset: Success')
                        console.info(
                            'Account Password Reset: Failed To Send Email'
                        )

                        setLoading(false)
                        setNotification({
                            type: 'inline',
                            visible: true,
                            kind: 'error',
                            title: 'Account Password Reset Failed:',
                            subtitle:
                                'Failed To Send Email, Please Try Again Or Contact Support',
                        })

                        console.log(
                            'Account Password Reset Failed: Failed To Send Email, Please Try Again Or Contact Support'
                        )
                    } else {
                        console.info('Account Password Reset: Failed')

                        setLoading(false)
                        setNotification({
                            type: 'inline',
                            visible: true,
                            kind: 'error',
                            title: 'Account Password Reset Failed:',
                            subtitle: 'Account Secret Answer Incorrect',
                        })

                        console.log(
                            'Account Password Reset Failed: Orchestrate Answer'
                        )
                    }
                } else {
                    console.info('Account Password Reset: Failed')

                    setLoading(false)
                    setNotification({
                        type: 'inline',
                        visible: true,
                        kind: 'error',
                        title: 'Account Password Reset Failed:',
                        subtitle:
                            'Please Fix All Validation Errors Before Trying To Resubmit',
                    })

                    console.log(
                        'Account Password Reset Invalid: Validation Is Invalid'
                    )
                }
            } else {
                console.info('Account Password Reset: Failed')

                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Account Password Reset Failed:',
                    subtitle:
                        'Please Fix All Validation Errors Before Trying To Resubmit',
                })

                console.log(
                    'Account Password Reset Invalid: Validation Is Invalid'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Modal Submit (Method)
    const handleModalSubmit = async () => {
        try {
            // Navigate To Next Form
            navigate(submit_route ? submit_route : '/')

            // Clear Existing Data
            handleClearData()
        } catch (error) {
            console.warn('Handle Modal Submit Error:', error)
            return null
        }
    }

    // Handle Modal Close (Method)
    const handleModalClose = async () => {
        try {
            setModalOpen(false)

            // Clear Existing Data
            handleClearData()
        } catch (error) {
            console.warn('Handle Modal Close Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Key Down Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <div className="form" data-testid="account-reset-password-page">
                    <h2
                        className="form-title"
                        title="page-heading"
                        role="heading"
                        aria-level="1"
                    >
                        {written_name ? written_name : ``}
                    </h2>
                    <LoadingComponent />
                </div>
            </>
        )
    } else if (answering) {
        return (
            <>
                <div
                    className="form"
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    data-testid="account-reset-password-page"
                >
                    <h2
                        className="form-title"
                        title="page-heading"
                        role="heading"
                        aria-level="1"
                    >
                        {written_name ? written_name : ``}
                    </h2>
                    <Form onSubmit={handleSubmit}>
                        <DividerComponent text="Account Credentials" />

                        <TextInput
                            className="required"
                            labelText="User Id:"
                            id="UserID"
                            name="UserID"
                            autocomplete="username"
                            placeholder="UserID"
                            value={accountData?.[digital_name]?.values?.UserID}
                            maxLength="50"
                            type="text"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors?.UserID
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.UserIDErrMsg
                            }
                            disabled={
                                accountData?.[digital_name]?.values
                                    ?.SecretQuestion
                                    ? true
                                    : false
                            }
                        />

                        <DividerComponent text="Password Recovery" />

                        <h4>
                            {accountData?.[digital_name]?.values?.SecretQuestion
                                ? accountData?.[digital_name]?.values
                                      ?.SecretQuestion
                                : ''}
                        </h4>

                        <TextInput
                            className="required"
                            labelText="Secret Answer:"
                            id="SecretAnswer"
                            name="SecretAnswer"
                            placeholder="Secret Answer"
                            value={
                                accountData?.[digital_name]?.values
                                    ?.SecretAnswer
                            }
                            maxLength="100"
                            type="text"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors
                                    ?.SecretAnswer
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.SecretAnswerErrMsg
                            }
                            role="textbox"
                            contentEditable="true"
                            aria-placeholder="Secret Answer"
                            aria-label="SecretAnswer"
                        />

                        <ModalComponent
                            modal={modalData}
                            open={modalOpen}
                            handleModalClose={handleModalClose}
                            handleModalSubmit={handleModalSubmit}
                        />

                        <NotificationComponent notification={notification} />

                        <div className="button-group">
                            <div className="button-group-item">
                                <ButtonComponent
                                    click={handleSubmit}
                                    title="Submit"
                                />
                            </div>
                        </div>
                    </Form>
                </div>

                <span
                    className="visually-hidden"
                    data-testid="account-reset-password-page-errors"
                    role="log"
                    aria-disabled="true"
                >
                    {JSON.stringify(accountData?.[digital_name]?.errors)}
                </span>
            </>
        )
    } else {
        return (
            <>
                <div
                    className="form"
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    data-testid="account-reset-password-page"
                >
                    <h2
                        className="form-title"
                        title="page-heading"
                        role="heading"
                        aria-level="1"
                    >
                        {written_name ? written_name : ``}
                    </h2>

                    <Form onSubmit={handleSubmit}>
                        <DividerComponent text="Account Credentials" />

                        <TextInput
                            className="required"
                            labelText="User Id:"
                            id="UserID"
                            name="UserID"
                            placeholder="UserID"
                            value={accountData?.[digital_name]?.values?.UserID}
                            maxLength="50"
                            type="text"
                            onChange={handleData}
                            invalid={
                                accountData?.[digital_name]?.errors?.UserID
                            }
                            invalidText={
                                accountData?.[digital_name]?.errors
                                    ?.UserIDErrMsg
                            }
                        />

                        <ModalComponent
                            modal={modalData}
                            open={modalOpen}
                            handleModalClose={handleModalClose}
                            handleModalSubmit={handleModalSubmit}
                        />

                        <NotificationComponent notification={notification} />

                        <div className="button-group">
                            <div className="button-group-item">
                                <ButtonComponent
                                    click={handleSubmit}
                                    title="Submit"
                                />
                            </div>
                        </div>
                    </Form>
                </div>

                <span
                    className="visually-hidden"
                    data-testid="account-reset-password-page-errors"
                    role="log"
                    aria-disabled="true"
                >
                    {JSON.stringify(accountData?.[digital_name]?.errors)}
                </span>
            </>
        )
    }
}

export default ResetPasswordPage
